import { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Grid, Pagination, Stack, Typography } from '@mui/material';

import { ErrorMessage } from '@components/base';
import { EmptyState } from '@components/feedback';
import useGlobalViewMode from '@hooks/useGlobalViewMode';
import { SimpleTitle } from '@models/Title';
import useSearchAndFilter from '@modules/layouts/hooks/useSearchAndFilter';
import { SearchCardActionProps } from '@modules/search/components/SearchResult/SearchGrid/SearchResultCard';

import OrderByPopup from '../OrderByPopup';
import OrderProvidersByPopup from '../OrderProvidersByPopup';
import GlobalViewMode from '../SearchToolbar/GlobalViewMode';

import SearchGrid from './SearchGrid';
import SearchList from './SearchList';

export type SearchResultsProps = {
  cardActions?: FC<SearchCardActionProps>;
  disableOrderProvidersBy?: boolean;
  disablePagination?: boolean;
  onSelect?: (simpleTitle: SimpleTitle) => void;
};

const SearchResult: FC<SearchResultsProps> = ({
  cardActions,
  disableOrderProvidersBy,
  disablePagination,
  onSelect,
}) => {
  const {
    queryResult: { error, isLoading, titles: { data, totalCount = 0 } = {} },
  } = useSearchAndFilter();

  const { t } = useTranslation();
  const { contentViewMode } = useGlobalViewMode();

  const ref = useRef<HTMLDivElement | null>(null);

  const {
    filterCount,
    pageNo,
    pageSize,
    provider,
    searchQuery: { queryText },
    setPageNo,
  } = useSearchAndFilter();

  const pageCount = Math.min(Math.ceil(totalCount / pageSize), 100000 / pageSize);

  return (
    <Stack ref={ref} spacing={1}>
      {data && (
        <div>
          <Grid alignItems='center' container spacing={1}>
            {!isLoading && data.length > 0 && (
              <Grid>
                <Typography color='text.secondary' variant='caption'>
                  <em>
                    {t('searchAndFilter.matchCount', {
                      count: data.length,
                      start: (pageNo - 1) * pageSize + 1,
                      end: (pageNo - 1) * pageSize + data.length,
                      totalCount,
                      context: data.length !== totalCount ? 'partial' : undefined,
                    })}
                  </em>
                </Typography>
              </Grid>
            )}

            <Grid size={{ xs: 0, sm: 'grow' }}>
              <Divider />
            </Grid>

            {!disableOrderProvidersBy && (
              <Grid ml='auto'>
                <OrderProvidersByPopup />
              </Grid>
            )}

            <Grid ml='auto'>
              <OrderByPopup />
            </Grid>

            <GlobalViewMode />
          </Grid>
        </div>
      )}

      {error ? (
        <ErrorMessage error={error} />
      ) : filterCount === 0 && !queryText && !provider ? (
        <EmptyState circleIcon description={t('search.emptyState')} icon='search' />
      ) : contentViewMode === 'grid' ? (
        <SearchGrid cardActions={cardActions} onSelect={onSelect} />
      ) : (
        <SearchList actions={cardActions} onSelect={onSelect} />
      )}

      {!disablePagination && pageCount > 1 && (
        <Box display='flex' justifyContent='center'>
          <Pagination count={pageCount} onChange={(_, page) => setPageNo(page)} page={pageNo} />
        </Box>
      )}
    </Stack>
  );
};

export default SearchResult;
